import { useState } from 'react';
import {
  Building2,
  KeyRound,
  LogOut,
  Mail,
  Monitor,
  ShieldCheck,
  UserCircle,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProfileSectionFio } from './ProfileSectionFio';
import { ProfileSectionEmail } from './ProfileSectionEmail';
import { ProfileSectionPassword } from './ProfileSectionPassword';
import { ProfileSectionTenant } from './ProfileSectionTenant';
import { ProfileSectionTwoFA } from './ProfileSectionTwoFA';
import { ProfileSectionSessions } from './ProfileSectionSessions';
import { useProfileMe } from '@/lib/api/profile';
import { api } from '@/lib/api';

/**
 * ProfileDrawer (AK-229) — единое окно профиля для всех ролей.
 *
 * Открывается из TopBar (аватар/имя справа). shadcn `<Sheet side="right">`
 * width ≈ 480px на десктопе (sm:max-w-md), full-width на мобиле.
 *
 * Разделы (collapsible через локальный state — не accordion-radio,
 * чтобы юзер мог редактировать несколько разделов параллельно):
 *   1. ФИО + телефон + язык + таймзона
 *   2. Email (запрос смены через письмо)
 *   3. Пароль (current + new + confirm)
 *   4. Активный tenant + список доступных
 *   5. 2FA — setup QR / disable
 *   6. Активные сессии — list + revoke
 *
 * Загружает `/profile/me` через TanStack Query (staleTime 30s).
 * Каждый раздел сам делает invalidate keys после мутаций — drawer не нужно
 * перезагружать руками.
 */
interface ProfileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SectionId = 'fio' | 'email' | 'password' | 'tenant' | '2fa' | 'sessions';

interface SectionDef {
  id: SectionId;
  title: string;
  Icon: typeof UserCircle;
}

const SECTIONS: SectionDef[] = [
  { id: 'fio', title: 'ФИО и контакты', Icon: UserCircle },
  { id: 'email', title: 'Email', Icon: Mail },
  { id: 'password', title: 'Пароль', Icon: KeyRound },
  { id: 'tenant', title: 'Активная компания', Icon: Building2 },
  { id: '2fa', title: 'Двухфакторная аутентификация', Icon: ShieldCheck },
  { id: 'sessions', title: 'Активные сессии', Icon: Monitor },
];

export function ProfileDrawer({ open, onOpenChange }: ProfileDrawerProps) {
  const profileQuery = useProfileMe({ enabled: open });
  const [openSection, setOpenSection] = useState<SectionId | null>('fio');

  const toggle = (id: SectionId) =>
    setOpenSection((curr) => (curr === id ? null : id));

  const handleLogout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } finally {
      window.location.href = '/login';
    }
  };

  const me = profileQuery.data;
  const displayName =
    me?.display_name ||
    [me?.last_name, me?.first_name].filter(Boolean).join(' ') ||
    me?.email ||
    '—';
  const initials = (me?.first_name?.[0] ?? '') + (me?.last_name?.[0] ?? '') ||
    me?.email?.slice(0, 2).toUpperCase() ||
    '??';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 w-full sm:max-w-md flex flex-col gap-0"
      >
        <SheetTitle className="sr-only">Профиль пользователя</SheetTitle>
        <SheetDescription className="sr-only">
          Управление профилем, email, паролем, 2FA и активными сессиями
        </SheetDescription>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border bg-secondary/40">
          {profileQuery.isLoading ? (
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          ) : profileQuery.isError ? (
            <Alert variant="destructive">
              <AlertDescription>
                Не удалось загрузить профиль. Попробуйте обновить страницу.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-center gap-3 min-w-0">
              <Avatar className="h-12 w-12">
                {me?.avatar_url && <AvatarImage src={me.avatar_url} alt={displayName} />}
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {initials.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p
                  className="font-semibold text-foreground truncate"
                  title={displayName}
                >
                  {displayName}
                </p>
                <p
                  className="text-xs text-muted-foreground truncate"
                  title={me?.email}
                >
                  {me?.email}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sections — scrollable */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {SECTIONS.map(({ id, title, Icon }) => {
            const isOpen = openSection === id;
            return (
              <div key={id} className="border-b border-border/60 last:border-b-0">
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
                  aria-expanded={isOpen}
                  aria-controls={`profile-section-${id}`}
                >
                  <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-sm text-foreground flex-1">
                    {title}
                  </span>
                  <span
                    className={`text-muted-foreground text-xs transition-transform ${
                      isOpen ? 'rotate-90' : ''
                    }`}
                    aria-hidden
                  >
                    ▶
                  </span>
                </button>
                {isOpen && me && (
                  <div
                    id={`profile-section-${id}`}
                    className="px-4 pb-4 pt-1"
                  >
                    {id === 'fio' && <ProfileSectionFio me={me} />}
                    {id === 'email' && <ProfileSectionEmail me={me} />}
                    {id === 'password' && (
                      <ProfileSectionPassword
                        hasPassword={me.has_password !== false}
                      />
                    )}
                    {id === 'tenant' && <ProfileSectionTenant me={me} />}
                    {id === '2fa' && <ProfileSectionTwoFA me={me} />}
                    {id === 'sessions' && <ProfileSectionSessions />}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <Separator />
        <div className="px-6 py-4">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Выйти из аккаунта
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default ProfileDrawer;
